import { Box, ClickAwayListener, Fade, Paper, Popper, Tooltip } from '@mui/material';
import { ReactNode, useRef, useState } from 'react';
import { LessRoundedFab } from './StyledComponents';

type Props = {
    btnIcon: NonNullable<ReactNode>;
    btnTitle: string;
    children: ReactNode;
};

export const PopperButton = ({ children, btnIcon, btnTitle }: Props) => {
    const [open, setOpen] = useState(false);
    const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const handleToggle = () => {
        setOpen(prevOpen => !prevOpen);
    };

    const handleClose = (event: globalThis.MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        if (!anchorRef.current?.contains(target)) {
            setOpen(false);
        }
    };

    return (
        <>
            <Tooltip title={btnTitle}>
                <LessRoundedFab
                    ref={anchorRef}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    color="primary"
                    size="small"
                >
                    {btnIcon}
                </LessRoundedFab>
            </Tooltip>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom"
                sx={theme => ({
                    zIndex: 1,
                    '&[x-placement*="top"] $arrow': {
                        bottom: 0,
                        left: 0,
                        marginBottom: '-0.9em',
                        width: '3em',
                        height: '1em',
                        '&::before': {
                            borderWidth: '1em 1em 0 1em',
                            borderColor: `${theme.palette.background.paper} transparent transparent transparent`
                        }
                    },
                    '&[x-placement*="bottom"] $arrow': {
                        top: 0,
                        left: 0,
                        marginTop: '-0.9em',
                        width: '3em',
                        height: '1em',
                        '&::before': {
                            borderWidth: '0 1em 1em 1em',
                            borderColor: `transparent transparent ${theme.palette.background.paper} transparent`
                        }
                    }
                })}
                modifiers={[
                    {
                        name: 'arrow',
                        enabled: true,
                        options: {
                            element: arrowRef
                        }
                    }
                ]}
                transition
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={event => handleClose(event)}>
                        <Fade {...TransitionProps} timeout={350}>
                            <Box component="div">
                                <Box
                                    component="span"
                                    sx={{
                                        position: 'absolute',
                                        fontSize: 7,
                                        width: '3em',
                                        height: '3em',
                                        '&::before': {
                                            content: '""',
                                            margin: 'auto',
                                            display: 'block',
                                            width: 0,
                                            height: 0,
                                            borderStyle: 'solid'
                                        }
                                    }}
                                    ref={setArrowRef}
                                />
                                <Paper sx={{ maxWidth: 400, overflow: 'auto' }}>{children}</Paper>
                            </Box>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};
